import { useEffect, useState, type FormEvent } from 'react'
import { Edit2, Loader2, Mail, Phone, Star, Trash2, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import { useTableSort } from '../../lib/useTableSort'
import type {
  ClientContactDto,
  CreateClientContactInput,
  UpdateClientContactInput,
} from '@jr2os/shared'
import Modal from '../ui/Modal'

interface Props {
  clientId: string
  canWrite: boolean
}

interface ListResponse {
  items: ClientContactDto[]
}

/**
 * Panel de contactos de un cliente. Carga GET /clients/:id/contacts y permite
 * crear / editar / borrar contactos, con un único marcado como "Principal".
 * El backend desmarca automáticamente los demás cuando se marca uno como
 * `isPrimary`, así que aquí basta con reload tras cada mutación.
 */
export default function ContactsPanel({ clientId, canWrite }: Props) {
  const { t } = useTranslation(['clients', 'common'])
  const [items, setItems] = useState<ClientContactDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<ClientContactDto | null>(null)

  useEffect(() => {
    let cancelled = false
    setItems(null)
    setError(null)
    api
      .get<ListResponse>(`/clients/${clientId}/contacts`)
      .then((res) => {
        if (cancelled) return
        setItems(res.items)
      })
      .catch((err) => {
        if (cancelled) return
        const msg =
          err instanceof ApiError
            ? extractError(err) ?? `Error ${err.status}`
            : err instanceof Error
              ? err.message
              : t('contacts.errors.load')
        setError(msg)
        setItems([])
      })
    return () => {
      cancelled = true
    }
  }, [clientId, t])

  async function reload() {
    try {
      const res = await api.get<ListResponse>(`/clients/${clientId}/contacts`)
      setItems(res.items)
      setError(null)
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? extractError(err) ?? `Error ${err.status}`
          : err instanceof Error
            ? err.message
            : t('contacts.errors.load')
      setError(msg)
    }
  }

  async function handleDelete(c: ClientContactDto) {
    if (
      !(await confirmDialog({
        message: t('contacts.confirmDelete', { name: c.name }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete<void>(`/clients/${clientId}/contacts/${c.id}`)
      await reload()
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? extractError(err) ?? `Error ${err.status}`
          : err instanceof Error
            ? err.message
            : t('contacts.errors.delete')
      toast.error(msg)
    }
  }

  // Panel dentro de la ficha del cliente: el orden vive en memoria (la URL no pinta nada aquí). Va por encima del retorno
  // temprano de «cargando»; `items` puede ser null y el hook lo admite.
  const { rows, header } = useTableSort(
    items,
    {
      name: (c) => c.name,
      role: (c) => c.role,
      email: (c) => c.email,
      phone: (c) => c.phone,
    },
    { persist: 'state' }
  )

  if (items === null) {
    return (
      <div className="t-muted flex items-center gap-2 py-6">
        <Loader2 size={16} className="animate-spin" /> {t('contacts.loading')}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="t-primary font-semibold text-base">{t('contacts.title')}</h3>
          <p className="t-muted text-xs mt-0.5">
            {t('contacts.description')}
          </p>
        </div>
        {canWrite && items.length > 0 && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5 shrink-0"
          >
            <UserPlus size={14} /> {t('contacts.newContact')}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300 text-xs px-3 py-2">
          {error}
        </div>
      )}

      {items.length === 0 ? (
        <div className="surface p-10 text-center">
          <UserPlus size={24} className="mx-auto t-faint mb-2" />
          <p className="t-muted text-sm mb-4">{t('contacts.empty')}</p>
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 inline-flex items-center gap-1.5"
            >
              <UserPlus size={14} /> {t('contacts.newContact')}
            </button>
          )}
        </div>
      ) : (
        <div className="surface overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base text-left t-muted text-[11px] uppercase tracking-wider">
                  {header('name', t('contacts.columns.name'), { className: 'px-4 py-2.5 font-medium' })}
                  {header('role', t('contacts.columns.role'), { className: 'px-4 py-2.5 font-medium' })}
                  {header('email', t('contacts.columns.email'), { className: 'px-4 py-2.5 font-medium' })}
                  {header('phone', t('contacts.columns.phone'), { className: 'px-4 py-2.5 font-medium' })}
                  {canWrite && <th className="px-4 py-2.5 font-medium w-20">{t('contacts.columns.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => canWrite && setEditing(c)}
                    className={[
                      'border-b border-base/50 last:border-b-0',
                      canWrite ? 'hover:bg-hover/50 cursor-pointer' : '',
                    ].join(' ')}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="t-primary font-medium">{c.name}</span>
                        {c.isPrimary && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-jr2-500/15 text-jr2-700 dark:text-jr2-300 border border-jr2-500/40 flex items-center gap-0.5">
                            <Star size={10} /> {t('contacts.primary')}
                          </span>
                        )}
                      </div>
                      {c.notes && (
                        <div className="t-faint text-xs mt-0.5 line-clamp-1">{c.notes}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 t-secondary">{c.role || '—'}</td>
                    <td className="px-4 py-3">
                      {c.email ? (
                        <a
                          href={`mailto:${c.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-jr2-700 dark:text-jr2-300 hover:underline flex items-center gap-1"
                        >
                          <Mail size={12} /> {c.email}
                        </a>
                      ) : (
                        <span className="t-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.phone ? (
                        <a
                          href={`tel:${c.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-jr2-700 dark:text-jr2-300 hover:underline flex items-center gap-1"
                        >
                          <Phone size={12} /> {c.phone}
                        </a>
                      ) : (
                        <span className="t-faint">—</span>
                      )}
                    </td>
                    {canWrite && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setEditing(c)}
                            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                            aria-label={t('common:actions.edit')}
                            title={t('common:actions.edit')}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(c)}
                            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-rose-400"
                            aria-label={t('common:actions.delete')}
                            title={t('common:actions.delete')}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && (
        <ContactFormModal
          mode="create"
          clientId={clientId}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false)
            void reload()
          }}
        />
      )}
      {editing && (
        <ContactFormModal
          mode="edit"
          clientId={clientId}
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void reload()
          }}
        />
      )}
    </div>
  )
}

// =============== Modal de creación / edición ===============

interface ContactFormModalProps {
  mode: 'create' | 'edit'
  clientId: string
  contact?: ClientContactDto
  onClose: () => void
  onSaved: () => void
}

function ContactFormModal({ mode, clientId, contact, onClose, onSaved }: ContactFormModalProps) {
  const { t } = useTranslation(['clients', 'common'])
  const [name, setName] = useState(contact?.name ?? '')
  const [role, setRole] = useState(contact?.role ?? '')
  const [email, setEmail] = useState(contact?.email ?? '')
  const [phone, setPhone] = useState(contact?.phone ?? '')
  const [notes, setNotes] = useState(contact?.notes ?? '')
  const [isPrimary, setIsPrimary] = useState(contact?.isPrimary ?? false)
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFormError(null)

    const trimmedName = name.trim()
    if (trimmedName.length < 2 || trimmedName.length > 200) {
      setFormError(t('contacts.form.errors.nameLength'))
      return
    }

    const payload = {
      name: trimmedName,
      role: role.trim() ? role.trim() : null,
      email: email.trim() ? email.trim() : null,
      phone: phone.trim() ? phone.trim() : null,
      notes: notes.trim() ? notes.trim() : null,
      isPrimary,
    }

    setLoading(true)
    try {
      if (mode === 'create') {
        await api.post<ClientContactDto>(
          `/clients/${clientId}/contacts`,
          payload as CreateClientContactInput,
        )
      } else if (contact) {
        await api.patch<ClientContactDto>(
          `/clients/${clientId}/contacts/${contact.id}`,
          payload as UpdateClientContactInput,
        )
      }
      onSaved()
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? extractError(err) ?? `Error ${err.status}`
          : err instanceof Error
            ? err.message
            : t('contacts.errors.save')
      setFormError(msg)
    } finally {
      setLoading(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-jr2-500/40 focus:border-jr2-500/60'

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'create' ? t('contacts.form.titleCreate') : t('contacts.form.titleEdit')}
      maxWidth="max-w-xl"
    >
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
        <div>
          <label className="block text-xs t-muted mb-1">{t('contacts.form.fields.nameRequired')}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
            autoFocus
            className={inputCls}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1">{t('contacts.form.fields.role')}</label>
            <input
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              maxLength={120}
              placeholder={t('contacts.form.placeholders.role')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1">{t('contacts.form.fields.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">{t('contacts.form.fields.phone')}</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={40}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">{t('contacts.form.fields.notes')}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            rows={3}
            className={`${inputCls} resize-y`}
          />
        </div>
        <label className="flex items-center gap-2 text-sm t-secondary cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            className="accent-jr2-500"
          />
          {t('contacts.form.markAsPrimary')}
        </label>

        {formError && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300 text-xs px-3 py-2">
            {formError}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)] disabled:opacity-50"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {mode === 'create' ? t('common:actions.create') : t('common:actions.save')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Extrae el mensaje de error de un ApiError cuyo body suele ser
 * `{ error: string, ... }` o `{ message: string }`. Devuelve null si no
 * encuentra nada legible.
 */
function extractError(err: ApiError): string | null {
  const body = err.body as { error?: string; message?: string } | null | undefined
  if (body?.error) return body.error
  if (body?.message) return body.message
  return null
}
